import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ThemeToggle from './ThemeToggle';
import { documentService } from '../services/documentService';
import { jobService } from '../services/jobService';

const FILE_ICONS = {
  'application/pdf': '📄',
  'application/msword': '📝',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '📝',
  'application/vnd.ms-excel': '📊',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '📊',
  'text/csv': '📊',
  'text/plain': '📃',
  'image/jpeg': '🖼️',
  'image/jpg': '🖼️',
  'image/png': '🖼️',
  'image/gif': '🖼️',
};

const fileIcon = (mime) => FILE_ICONS[mime] || '📎';

const formatBytes = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const Documents = () => {
  const [documents, setDocuments] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadJobId, setUploadJobId] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [filterJobId, setFilterJobId] = useState('');
  const fileInputRef = useRef(null);

  const { user, logout } = useAuth();

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const data = await documentService.getDocuments();
      setDocuments(data.data || []);
      setError('');
    } catch {
      setError('Failed to load documents. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDocuments();
    jobService.getJobs().then((res) => setJobs(res.data || [])).catch(() => setJobs([]));
  }, [fetchDocuments]);

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadFiles.length) return;
    setUploading(true);
    setUploadError('');
    try {
      for (const file of uploadFiles) {
        const formData = new FormData();
        formData.append('file', file);
        if (uploadJobId) formData.append('job_id', uploadJobId);
        await documentService.uploadDocument(formData);
      }
      await fetchDocuments();
      setShowUploadModal(false);
      setUploadFiles([]);
      setUploadJobId('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      showSuccess(`${uploadFiles.length} document${uploadFiles.length !== 1 ? 's' : ''} uploaded successfully!`);
    } catch (err) {
      const errors = err.response?.data?.errors;
      if (errors?.file) {
        setUploadError(errors.file[0]);
      } else {
        setUploadError(err.response?.data?.message || 'Upload failed. Please try again.');
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await documentService.deleteDocument(id);
      setDocuments((prev) => prev.filter((d) => d.id !== id));
      setDeleteConfirm(null);
      showSuccess('Document deleted.');
    } catch {
      setError('Failed to delete document.');
    }
  };

  const handleDownload = async (doc) => {
    try {
      await documentService.downloadDocument(doc.id, doc.original_name);
    } catch {
      setError('Failed to download document.');
    }
  };

  const closeUploadModal = () => {
    setShowUploadModal(false);
    setUploadFiles([]);
    setUploadJobId('');
    setUploadError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const displayedDocuments = filterJobId
    ? documents.filter((d) => String(d.job_id) === filterJobId)
    : documents;

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-6xl mx-auto px-2.5 py-5">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-1.5 text-sm">
              <Link to="/dashboard" className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">Dashboard</Link>
              <span className="text-gray-400 dark:text-gray-600">/</span>
              <span className="text-gray-900 dark:text-white font-medium">Documents</span>
            </div>
            <div className="flex flex-wrap items-center gap-3 sm:gap-4 w-full sm:w-auto">
              <ThemeToggle />
              <span className="text-gray-600 dark:text-gray-300 font-medium text-sm sm:text-base order-last sm:order-none">{user?.name}</span>
              <button
                onClick={logout}
                className="bg-red-500 text-white border-0 rounded-md px-3 sm:px-4 py-2 text-sm font-medium cursor-pointer transition-all duration-200 hover:bg-red-600"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-2.5 py-8">
        {successMessage && (
          <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 text-green-700 dark:text-green-300 text-sm font-medium">
            ✓ {successMessage}
          </div>
        )}
        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>
        )}

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Documents</h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
              {displayedDocuments.length} {displayedDocuments.length === 1 ? 'document' : 'documents'}
              {filterJobId && ' (filtered)'}
            </p>
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            {jobs.length > 0 && (
              <select
                value={filterJobId}
                onChange={(e) => setFilterJobId(e.target.value)}
                className="flex-1 sm:flex-none px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All documents</option>
                {jobs.map((j) => (
                  <option key={j.id} value={String(j.id)}>{j.title}</option>
                ))}
              </select>
            )}
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity shadow-md whitespace-nowrap"
            >
              <span className="text-lg leading-none">↑</span>
              Upload Document
            </button>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="flex items-center gap-3 text-gray-500 dark:text-gray-400">
                <div className="w-5 h-5 border-2 border-gray-300 dark:border-gray-600 border-t-blue-500 rounded-full animate-spin"></div>
                Loading documents...
              </div>
            </div>
          ) : displayedDocuments.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="text-5xl mb-4">📁</div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No documents yet</h3>
              <p className="text-gray-500 dark:text-gray-400 mb-6">Upload your first document to get started.</p>
              <button
                onClick={() => setShowUploadModal(true)}
                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity"
              >
                <span className="text-lg leading-none">↑</span>
                Upload Document
              </button>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">File</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Job</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Size</th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Uploaded</th>
                      <th className="text-right px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {displayedDocuments.map((doc) => (
                      <tr key={doc.id} className="text-sm hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <span className="text-xl">{fileIcon(doc.mime_type)}</span>
                            <span className="font-medium text-gray-900 dark:text-white truncate max-w-xs">{doc.original_name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">
                          {doc.job
                            ? <Link to={`/jobs/${doc.job_id}`} className="text-blue-600 dark:text-blue-400 hover:underline">{doc.job.title}</Link>
                            : <span className="text-gray-400 dark:text-gray-500 italic">No job</span>}
                        </td>
                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">{formatBytes(doc.size)}</td>
                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">{formatDate(doc.created_at)}</td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => handleDownload(doc)}
                              className="px-3 py-1.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors font-medium"
                            >
                              Download
                            </button>
                            {deleteConfirm === doc.id ? (
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-red-600 dark:text-red-400">Delete?</span>
                                <button onClick={() => handleDelete(doc.id)} className="text-xs font-semibold text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 transition-colors">Yes</button>
                                <button onClick={() => setDeleteConfirm(null)} className="text-xs font-semibold text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">No</button>
                              </div>
                            ) : (
                              <button
                                onClick={() => setDeleteConfirm(doc.id)}
                                className="px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors font-medium"
                              >
                                Delete
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
                {displayedDocuments.map((doc) => (
                  <div key={doc.id} className="p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-2xl shrink-0">{fileIcon(doc.mime_type)}</span>
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-900 dark:text-white truncate">{doc.original_name}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{formatBytes(doc.size)} · {formatDate(doc.created_at)}</p>
                          {doc.job
                            ? <Link to={`/jobs/${doc.job_id}`} className="text-xs text-blue-600 dark:text-blue-400 hover:underline">{doc.job.title}</Link>
                            : <span className="text-xs text-gray-400 dark:text-gray-500 italic">No job</span>}
                        </div>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button onClick={() => handleDownload(doc)} className="text-sm text-blue-600 dark:text-blue-400 font-medium">Download</button>
                        {deleteConfirm === doc.id ? (
                          <div className="flex items-center gap-1.5">
                            <button onClick={() => handleDelete(doc.id)} className="text-xs font-semibold text-red-600 dark:text-red-400">Yes</button>
                            <button onClick={() => setDeleteConfirm(null)} className="text-xs font-semibold text-gray-500 dark:text-gray-400">No</button>
                          </div>
                        ) : (
                          <button onClick={() => setDeleteConfirm(doc.id)} className="text-sm text-red-600 dark:text-red-400 font-medium">Delete</button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </main>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Upload Document</h3>

            {uploadError && (
              <div className="mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-red-700 dark:text-red-300 text-sm">
                {uploadError}
              </div>
            )}

            <form onSubmit={handleUpload} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  File <span className="text-red-500">*</span>
                </label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
                  multiple
                  required
                  onChange={(e) => setUploadFiles(Array.from(e.target.files || []))}
                  className="w-full text-sm text-gray-700 dark:text-gray-300 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 dark:file:bg-blue-900/30 dark:file:text-blue-300 hover:file:bg-blue-100 dark:hover:file:bg-blue-900/50 cursor-pointer"
                />
                {uploadFiles.length > 1 && (
                  <p className="mt-1 text-xs text-blue-600 dark:text-blue-400">{uploadFiles.length} files selected</p>
                )}
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Accepted: images, PDF, Word, Excel, CSV, TXT. Max 20 MB each.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Link to Job <span className="text-gray-400 dark:text-gray-500 font-normal">(optional)</span>
                </label>
                <select
                  value={uploadJobId}
                  onChange={(e) => setUploadJobId(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">No job</option>
                  {jobs.map((j) => (
                    <option key={j.id} value={j.id}>{j.title}</option>
                  ))}
                </select>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeUploadModal}
                  className="flex-1 px-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading || !uploadFiles.length}
                  className="flex-1 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploading ? 'Uploading…' : uploadFiles.length > 1 ? `Upload ${uploadFiles.length} Files` : 'Upload'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Documents;
